import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { useCartValidations } from '@/shared/composables/cart/useCartValidations';
import {
  addToCartFailureReasons,
  cartUpdateFailureReasons,
  LEGACY_CART_ATTRIBUTE_AOV_AWARD_SHOWN,
  posthogEvents
} from '@/shared/constants/constants';
import { CartApiService } from '@/shared/services/cartAPI';
import {
  computeShippingSubtotal,
  findPickedRewardLineNumber,
  getGiftThresholdFromDOM,
  getLineItemUpdateKey
} from '@/shared/utils/cart';
import { getErrorMessage } from '@/shared/utils/error';

import { useProductStore } from './product';
import { useProductOptionsStore } from './product-options';
import { useProductQuantitiesStore } from './product-quantities';

export type AddToCartFailureReason =
  (typeof addToCartFailureReasons)[keyof typeof addToCartFailureReasons];
type CartUpdateFailureReason =
  (typeof cartUpdateFailureReasons)[keyof typeof cartUpdateFailureReasons];

export const useCartStore = defineStore('cart', () => {
  const {
    validateAddPayload,
    validateChangePayload,
    checkAndFetchPointsPricesWhenNeeded,
    getPointPriceForItemSync
  } = useCartValidations();
  const _cart = ref<ShopifyCart | null>(null);
  const _errors = ref<CartLineError[]>([]);
  /** Gift threshold in cents; set by threshold/gift components when they mount. Used to remove _pickedReward when threshold is no longer met after a quantity change. */
  const _giftThreshold = ref<number>(0);

  const loadingCart = ref(false);
  const miniCartOpen = ref(false);
  const cart = computed(() => _cart.value);
  const errors = computed(() => _errors.value);
  const giftThreshold = computed(() => _giftThreshold.value);

  const setGiftThreshold = (value: number) => {
    _giftThreshold.value = value;
  };

  watch(
    () => cart.value,
    (newCart) => {
      if (newCart?.items) {
        checkAndFetchPointsPricesWhenNeeded(newCart.items).catch(() => {});
      }
    },
    { deep: true }
  );

  const clearErrors = () => {
    _errors.value = [];
  };

  const clearLineError = (lineKey: string) => {
    _errors.value = _errors.value.filter((e) => e.lineKey !== lineKey);
  };

  // Cart reads and writes run one at a time. Each queued task sees the result of the
  // previous one, so a mutation never rebuilds its payload from a stale snapshot and a
  // read never clears the loading flag while another task is still pending.
  let mutationChain: Promise<void> = Promise.resolve();
  let pendingMutations = 0;

  const enqueue = (task: () => Promise<void>): Promise<void> => {
    pendingMutations += 1;
    loadingCart.value = true;

    const run = mutationChain.then(task);
    mutationChain = run.catch(() => {});

    return run.finally(() => {
      pendingMutations -= 1;
      if (pendingMutations === 0) {
        loadingCart.value = false;
      }
    });
  };

  const loadCartFromApi = async () => {
    const cart = await CartApiService.getCart();

    if (cart) {
      _cart.value = cart;

      // One-time cleanup: remove legacy attribute so it stops appearing in requests.
      if (cart.attributes?.[LEGACY_CART_ATTRIBUTE_AOV_AWARD_SHOWN]) {
        try {
          const updatedCart = await CartApiService.removeCartAttribute(
            LEGACY_CART_ATTRIBUTE_AOV_AWARD_SHOWN
          );
          if (updatedCart) {
            _cart.value = updatedCart;
          }
        } catch {
          // Non-critical: cart is already set; attribute will be removed on a later load
        }
      }
    }
  };

  const getCart = () => enqueue(loadCartFromApi);

  const addIndependentProductsToCart = async (items: ShopifyCartItem[]) => {
    const payload = { items };
    // no validation needed, only pattern related products are independent, those can't be point products
    return await addToCart(payload);
  };

  const addMainProductToCart = async () => {
    const productStore = useProductStore();
    const quantityStore = useProductQuantitiesStore();
    const optionsStore = useProductOptionsStore();

    const id: number | null =
      optionsStore.activeOption?.id ||
      (productStore.variantId
        ? (() => {
            const parsed = parseInt(productStore.variantId);
            return isNaN(parsed) ? null : parsed;
          })()
        : null);

    // check if product is already in the cart, if product is a pattern.
    // Expected business rule, so it stays outside the failure tracking below.
    if (productStore.isPattern) {
      const addedToCartAlready =
        cart.value?.items.find((item) => item.id == productStore.variantId)
          ?.quantity || 0;
      if (addedToCartAlready > 0) {
        throw new Error(window.Hobbii?.errorMessages.errorAlreadyAdded);
      }
    }

    let failureReason: AddToCartFailureReason =
      addToCartFailureReasons.MISSING_VARIANT;

    try {
      if (!id) {
        throw new Error(window.Hobbii?.errorMessages.unknownErrorOccurred);
      }

      const payload: ShopifyCartAddPayload = {
        items: [
          {
            id,
            quantity: quantityStore.quantity
          }
        ]
      };

      // add properties to the item to help point products be processed
      if (productStore.isPointProduct) {
        // when adding mainProduct, there's always only one item in the payload
        const itemBeingAdded = payload.items[0];

        //this property is not exposed to the user, thus using prefix '_'
        itemBeingAdded.properties = {
          _pointsPrice:
            (productStore.pointsPrice && productStore.pointsPrice + '') || '0'
        };

        if (window.Hobbii?.cart.messages) {
          payload.items[0].properties = {
            ...payload.items[0].properties,
            [window.Hobbii.cart.messages.pointProduct]:
              window.Hobbii?.cart.messages.paidWithPoints
          };
        }
      }

      failureReason = addToCartFailureReasons.VALIDATION;
      await validateAddPayload(payload, cart.value?.items || []);

      failureReason = addToCartFailureReasons.REQUEST;
      return await addToCart(payload);
    } catch (error) {
      window.posthog?.capture(posthogEvents.ADD_TO_CART_FAILED, {
        reason: failureReason,
        variant_id: id,
        product_id: productStore.productId,
        product_type: productStore.entityType,
        quantity: quantityStore.quantity,
        error_message: getErrorMessage(error)
      });
      throw error;
    }
  };

  const addToCart = async (payload: ShopifyCartAddPayload) => {
    const result = await CartApiService.addToCart(payload);
    if (result) {
      _cart.value = result;
    }
    return result;
  };

  const isFreeGiftItem = (lineNumber: number) => {
    const item = cart.value?.items[lineNumber - 1];

    if (!item) return false;

    return item.properties?._pickedReward === 'true';
  };

  const trackFreeGiftRemoved = (lineNumber: number) => {
    const item = cart.value?.items[lineNumber - 1];

    if (!item) return;

    if (isFreeGiftItem(lineNumber)) {
      window.posthog?.capture(posthogEvents.GIFT_REMOVED, {
        product_id: item.product_id,
        variant_id: item.variant_id,
        product_name: item.title
      });
    }
  };

  /**
   * Projected shipping subtotal if we apply a quantity change to one line (without making the API call).
   * Replaces that line's contribution with (newQuantity * unit price).
   */
  const projectedShippingSubtotal = (
    items: ShopifyCartItem[],
    lineNumber: number,
    newQuantity: number
  ): number => {
    const line = items[lineNumber - 1];
    if (!line) return computeShippingSubtotal(items);
    const isGift = line.properties?._pickedReward === 'true';
    const isGiftCard = !!(line.gift_card || line.product?.gift_card);
    const isDigital =
      line.product?.metafields?.hobbii?.entity_type === 'pattern' ||
      isGiftCard ||
      (line.requires_shipping === false && !isGiftCard);
    const currentContribution =
      line.requires_shipping && !isDigital && !isGift
        ? (line.final_line_price ?? 0)
        : 0;
    const unitPrice = line.quantity
      ? (line.final_line_price ?? 0) / line.quantity
      : 0;
    const newContribution =
      line.requires_shipping && !isDigital && !isGift
        ? newQuantity * unitPrice
        : 0;
    return (
      computeShippingSubtotal(items) - currentContribution + newContribution
    );
  };

  const refreshCartSilently = async () => {
    const fresh = await CartApiService.getCart();
    if (fresh) {
      _cart.value = fresh;
    }
  };

  const recordLineError = (
    lineKey: string,
    message: string,
    quantity: number,
    item: ShopifyCartItem | undefined,
    reason: CartUpdateFailureReason
  ) => {
    _errors.value = _errors.value.filter((e) => e.lineKey !== lineKey);
    _errors.value.push({ lineKey, message });
    window.posthog?.capture(posthogEvents.CART_UPDATE_FAILED, {
      reason,
      requested_quantity: quantity,
      variant_id: item?.variant_id ?? item?.id,
      product_id: item?.product_id,
      message
    });
  };

  const performCartChangeQty = async (
    requestedKey: string | null,
    requestedLineNumber: number,
    quantity: number
  ) => {
    const items = cart.value?.items ?? [];
    // Prefer the line key captured at click time; fall back to position when the cart
    // had not loaded yet (so no earlier mutation could have shifted the lines).
    const lineIndex = requestedKey
      ? items.findIndex((item) => getLineItemUpdateKey(item) === requestedKey)
      : requestedLineNumber - 1;

    // The line is gone (removed by an earlier queued mutation, or out of range).
    if (lineIndex < 0 || lineIndex >= items.length) {
      if (requestedKey) {
        clearLineError(requestedKey);
      }
      return;
    }

    const lineItem = items[lineIndex];
    const lineKey = getLineItemUpdateKey(lineItem);
    const lineNumber = lineIndex + 1;
    clearLineError(lineKey);

    const payload = { line: lineNumber, quantity };
    try {
      await validateChangePayload(payload, items);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      recordLineError(
        lineKey,
        message,
        quantity,
        lineItem,
        cartUpdateFailureReasons.VALIDATION
      );
      await refreshCartSilently();
      return;
    }

    const isFreeGift = isFreeGiftItem(lineNumber);
    if (isFreeGift && quantity === 0) {
      trackFreeGiftRemoved(lineNumber);
    }

    let threshold = _giftThreshold.value;
    if (threshold <= 0) {
      const fromDom = getGiftThresholdFromDOM();
      if (fromDom > 0) {
        _giftThreshold.value = fromDom;
        threshold = fromDom;
      }
    }
    const giftLineNumber = findPickedRewardLineNumber(items);
    const needToRemoveGift =
      !isFreeGift &&
      threshold > 0 &&
      giftLineNumber !== null &&
      projectedShippingSubtotal(items, lineNumber, quantity) < threshold;

    const updates: Record<string, number> = {};
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const qty =
        needToRemoveGift && giftLineNumber !== null
          ? i === lineNumber - 1
            ? quantity
            : i === giftLineNumber - 1
              ? 0
              : item.quantity
          : i === lineNumber - 1
            ? quantity
            : item.quantity;
      updates[getLineItemUpdateKey(item)] = qty;
    }

    const updateResponse = await CartApiService.updateCartQuantities(updates);

    if (updateResponse.cart) {
      _cart.value = updateResponse.cart;
      clearLineError(lineKey);
      return;
    }

    if (updateResponse.error) {
      recordLineError(
        lineKey,
        updateResponse.error.message,
        quantity,
        lineItem,
        cartUpdateFailureReasons.UPDATE
      );
    }
    await refreshCartSilently();
  };

  const cartChangeQty = (
    lineNumber: number,
    quantity: number
  ): Promise<void> => {
    const item = cart.value?.items[lineNumber - 1];
    // Resolve a stable line key now, while `lineNumber` still matches the rendered
    // cart; positions may shift before this mutation reaches the front of the queue.
    // When the cart has not loaded yet, defer to a position lookup at run time.
    const lineKey = item ? getLineItemUpdateKey(item) : null;

    return enqueue(() => performCartChangeQty(lineKey, lineNumber, quantity));
  };

  const getPointPriceForItem = (item: ShopifyCartItem): number =>
    getPointPriceForItemSync(item);

  return {
    loadingCart, // convert to computed when possible
    cart,
    miniCartOpen,
    errors,
    giftThreshold,
    setGiftThreshold,
    getCart,
    addToCart,
    addMainProductToCart,
    addIndependentProductsToCart,
    cartChangeQty,
    clearErrors,
    clearLineError,
    getPointPriceForItem
  };
});
